import { useState, useEffect, ReactNode } from 'react';
import { useParams, Link, useLocation, Outlet } from 'react-router-dom';
import { repoApi } from '../../api/client';
import { refToSegment } from '../../utils/refSegment';
import { RepoProvider, type Repository, type Branch, resolveDefaultBranch } from '../../contexts/RepoContext';
import './RepoLayout.css';

function getApiOrigin(): string {
    const apiBase = import.meta.env.VITE_API_BASE;
    if (apiBase && typeof apiBase === 'string' && (apiBase.startsWith('http://') || apiBase.startsWith('https://'))) {
        try {
            return new URL(apiBase).origin;
        } catch {
            return window.location.origin;
        }
    }
    return window.location.origin;
}

interface RepoLayoutProps {
    repoPath?: string;
    children?: ReactNode;
}

export function RepoLayout({ repoPath: repoPathProp, children }: RepoLayoutProps) {
    const params = useParams<{ owner?: string; repo?: string }>();
    const location = useLocation();
    const repoPath = repoPathProp || (params.owner && params.repo ? `${params.owner}/${params.repo}` : '');

    const [repo, setRepo] = useState<Repository | null>(null);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [currentBranch, setCurrentBranch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const loadRepo = async () => {
        if (!repoPath) return;
        try {
            setLoading(true);
            setError(null);
            try {
                const home = await repoApi.getHome(repoPath);
                setRepo(home.repo);
                setBranches(home.branches);
                setCurrentBranch(resolveDefaultBranch(home.repo.defaultBranch, home.branches));
            } catch {
                const [fetchedRepo, branchesData] = await Promise.all([
                    repoApi.get(repoPath),
                    repoApi.getBranches(repoPath).catch(() => []),
                ]);
                setRepo(fetchedRepo);
                setBranches(branchesData);
                setCurrentBranch(resolveDefaultBranch(fetchedRepo.defaultBranch, branchesData));
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : '加载仓库失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (repoPath) loadRepo();
    }, [repoPath]);

    if (!repoPath) {
        return <>{children}</>;
    }

    if (loading) {
        const isCommitsRoute = location.pathname.includes('/-/commits/');
        const skeletonContext = {
            repoPath,
            repo: null,
            branches: [],
            currentBranch: 'main',
            setCurrentBranch: () => {},
            defaultBranch: 'main',
            cloneUrl: '',
            loading: true,
            error: null,
            refetch: loadRepo,
        };
        return (
            <div className="repo-layout">
                <RepoProvider value={skeletonContext}>
                    <div className="repo-layout-chrome repo-layout-skeleton">
                        <div className="repo-layout-page repo-page">
                            <div className="repo-header">
                                <div className="repo-title">
                                    <div className="repo-icon repo-skeleton-pulse" />
                                    <div className="repo-breadcrumb">
                                        <div className="repo-skeleton-line repo-skeleton-breadcrumb" />
                                        <div className="repo-skeleton-line repo-skeleton-desc" />
                                    </div>
                                </div>
                            </div>
                            <div className="clone-section">
                                <div className="repo-skeleton-line repo-skeleton-clone" />
                            </div>
                            <div className="tabs repo-layout-tabs">
                                <span className="tab repo-skeleton-tab" />
                                <span className="tab repo-skeleton-tab" />
                                <span className="tab repo-skeleton-tab" />
                                <span className="tab repo-skeleton-tab" />
                                <span className="tab repo-skeleton-tab" />
                            </div>
                        </div>
                    </div>
                    <div className="repo-layout-content">
                        {isCommitsRoute ? <CommitHistoryLoadingSkeleton /> : <DefaultLoadingSkeleton />}
                    </div>
                </RepoProvider>
            </div>
        );
    }

    if (error || !repo) {
        return (
            <div className="repo-layout">
                <div className="repo-layout-error">
                    <h2>无法访问该仓库</h2>
                    <p>{error || '仓库不存在或您没有访问权限'}</p>
                    <Link to="/" className="btn btn-primary">返回首页</Link>
                </div>
            </div>
        );
    }

    const cloneUrl = `${getApiOrigin()}/${repo.fullPath}.git`;
    const value = {
        repoPath,
        repo,
        branches,
        currentBranch,
        setCurrentBranch,
        defaultBranch: repo.defaultBranch,
        cloneUrl,
        loading: false,
        error: null,
        refetch: loadRepo,
    };

    return (
        <RepoProvider value={value}>
            <RepoLayoutChrome
                repo={repo}
                branches={branches}
                currentBranch={currentBranch}
                cloneUrl={cloneUrl}
            />
            <div className="repo-layout-content">
                {children ?? <Outlet />}
            </div>
        </RepoProvider>
    );
}

function DefaultLoadingSkeleton() {
    return (
        <div className="repo-loading-content" aria-hidden="true">
            <div className="repo-skeleton-line repo-loading-title" />
            <div className="repo-skeleton-line repo-loading-block" />
            <div className="repo-skeleton-line repo-loading-block" />
        </div>
    );
}

function CommitHistoryLoadingSkeleton() {
    return (
        <div className="repo-loading-content repo-commits-loading" aria-hidden="true">
            <div className="repo-commits-loading-header">
                <div className="repo-skeleton-line repo-commits-loading-title" />
                <div className="repo-skeleton-line repo-commits-loading-branch" />
            </div>
            <div className="repo-commits-loading-list">
                {Array.from({ length: 5 }).map((_, idx) => (
                    <div className="repo-commits-loading-item" key={idx}>
                        <div className="repo-skeleton-pulse repo-commits-loading-avatar" />
                        <div className="repo-commits-loading-main">
                            <div className="repo-skeleton-line repo-commits-loading-message" />
                            <div className="repo-skeleton-line repo-commits-loading-meta" />
                        </div>
                        <div className="repo-skeleton-line repo-commits-loading-sha" />
                    </div>
                ))}
            </div>
        </div>
    );
}

function RepoLayoutChrome({
    repo,
    branches,
    currentBranch,
    cloneUrl,
}: {
    repo: Repository;
    branches: Branch[];
    currentBranch: string;
    cloneUrl: string;
}) {
    const location = useLocation();
    const pathSegments = repo.fullPath.split('/');

    const isActive = (pathSuffix: string) => {
        const full = `/${repo.fullPath}/-/${pathSuffix}`;
        return location.pathname === full || location.pathname.startsWith(full + '/');
    };

    return (
        <div className="repo-layout-chrome">
            <div className="repo-layout-page repo-page">
                <div className="repo-header">
                    <div className="repo-title">
                        <div className="repo-icon">
                            <svg viewBox="0 0 16 16" width="24" height="24">
                                <path fill="currentColor" d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Zm10.5-1h-8a1 1 0 0 0-1 1v6.708A2.486 2.486 0 0 1 4.5 9h8ZM5 12.25a.25.25 0 0 1 .25-.25h3.5a.25.25 0 0 1 .25.25v3.25a.25.25 0 0 1-.4.2l-1.45-1.087a.249.249 0 0 0-.3 0L5.4 15.7a.25.25 0 0 1-.4-.2Z" />
                            </svg>
                        </div>
                        <div className="repo-breadcrumb">
                            <h1>
                                {pathSegments.map((segment, i) => {
                                    const isLast = i === pathSegments.length - 1;
                                    const pathSoFar = pathSegments.slice(0, i + 1).join('/');
                                    const href = isLast ? `/${repo.fullPath}` : `/groups/${pathSoFar}`;
                                    return (
                                        <span key={pathSoFar}>
                                            {i > 0 && <span className="repo-breadcrumb-sep">/</span>}
                                            <Link to={href} className={isLast ? 'repo-breadcrumb-repo' : 'repo-breadcrumb-link'}>
                                                {segment}
                                            </Link>
                                        </span>
                                    );
                                })}
                            </h1>
                            {repo.description && (
                                <p className="repo-description">{repo.description}</p>
                            )}
                        </div>
                    </div>
                    <div className="repo-actions">
                        {repo.isPrivate && (
                            <span className="private-badge">私有</span>
                        )}
                        <button
                            className="btn btn-secondary"
                            onClick={() => navigator.clipboard.writeText(cloneUrl)}
                            title="复制克隆 URL"
                        >
                            克隆
                        </button>
                    </div>
                </div>

                <div className="clone-section">
                    <div className="clone-input-group">
                        <span className="clone-label">HTTPS</span>
                        <input type="text" value={cloneUrl} readOnly className="clone-input" />
                        <button
                            className="copy-btn"
                            onClick={() => navigator.clipboard.writeText(cloneUrl)}
                            title="复制"
                        >
                            <svg viewBox="0 0 16 16" width="16" height="16">
                                <path fill="currentColor" d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 0 1 0 1.5h-1.5a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-1.5a.75.75 0 0 1 1.5 0v1.5A1.75 1.75 0 0 1 9.25 16h-7.5A1.75 1.75 0 0 1 0 14.25Z" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="tabs repo-layout-tabs">
                    <Link
                        to={`/${repo.fullPath}`}
                        className={`tab ${location.pathname === `/${repo.fullPath}` ? 'active' : ''}`}
                    >
                        代码
                    </Link>
                    <Link
                        to={`/${repo.fullPath}/-/branches`}
                        className={`tab ${isActive('branches') ? 'active' : ''}`}
                    >
                        分支 ({branches.length})
                    </Link>
                    <Link
                        to={`/${repo.fullPath}/-/commits/${refToSegment(currentBranch || repo.defaultBranch || 'main')}`}
                        className={`tab ${location.pathname.includes('/commits') ? 'active' : ''}`}
                    >
                        提交历史
                    </Link>
                    <Link
                        to={`/${repo.fullPath}/-/pulls`}
                        className={`tab ${isActive('pulls') ? 'active' : ''}`}
                    >
                        合并请求
                    </Link>
                    <Link
                        to={`/${repo.fullPath}/-/settings`}
                        className={`tab ${isActive('settings') ? 'active' : ''}`}
                    >
                        设置
                    </Link>
                </div>
            </div>
        </div>
    );
}
